import React, { useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const ContactSection = () => {
  const sectionRefContact = useRef(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '+92 ',
    nationality: '',
    role: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: null, message: '' });

  // Animation for the heading lines
  useGSAP(() => {
    gsap.registerPlugin(ScrollTrigger);

    gsap.from(sectionRefContact.current, {
      y: 100,
      opacity: 0,
      duration: 1.5,
      stagger: 0.1,
      ease: "power3.out",
      scrollTrigger: {
        trigger: "#contact-us",
        start: "top 80%",
      }
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    
    // Ensure +92 remains and only allow numbers after it
    if (value.startsWith('+92 ') && /^\+92 \d{0,10}$/.test(value)) {
      setFormData(prev => ({
        ...prev,
        phone: value
      }));
    } else if (value.length < 4) {
      // If user tries to delete +92, reset it
      setFormData(prev => ({
        ...prev,
        phone: '+92 '
      }));
    }
  };

  const handlePhoneKeyDown = (e) => {
    // Prevent backspace from deleting +92
    if (e.key === 'Backspace' && formData.phone.length <= 4) {
      e.preventDefault();
    }
    
    // Allow only numbers, backspace, delete, tab, arrows
    if (!/[0-9]|Backspace|Delete|Tab|ArrowLeft|ArrowRight/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ success: null, message: '' });

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/contact/submit-contact-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        // Reset form on successful submission
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '+92 ',
          nationality: '',
          role: '',
          message: ''
        });
        setSubmitStatus({ success: true, message: result.message || 'Your message has been sent successfully!' });
      } else {
        setSubmitStatus({ success: false, message: result.message || 'Failed to submit form. Please try again.' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ success: false, message: 'An error occurred. Please try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id='contact-us' className="bg-white py-32 light-section">
      <div className="container w-[80%] px-6 flex items-start justify-between bg-white gap-x-4 py-16 mx-auto">
        {/* Left Content */}
        <div className="animate-lines w-[40%] mb-8 md:mb-0 overflow-hidden pr-10">
          <h1 ref={sectionRefContact} className="text-[90px] md:text-[75px] font-bold text-black leading-[1] tracking-tight">
            <div className="">CONTACT</div>
            <div className="">US NOW</div>
          </h1>
          <p className="text-gray-900 mt-4 text-[24px] leading-[1.1] tracking-tight">
            Got questions or ready to take the next step in your study abroad journey?<br /> 
            Reach out to us today, Socraticali Consultants will guide you every step of the way!
          </p>
        </div>

        {/* Form Section */}
        <div className="w-[60%] p-6 rounded-lg text-black">
          {submitStatus.success !== null && (
            <div 
              className={`mb-4 p-3 rounded-md ${submitStatus.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
            >
              {submitStatus.message}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4">
              <input 
                type="text" 
                name="firstName"
                placeholder="Enter your first name" 
                className="p-3 w-full rounded-md border bg-neutral-100"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
              <input 
                type="text" 
                name="lastName"
                placeholder="Enter your last name" 
                className="p-3 w-full rounded-md border bg-neutral-100"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </div>
            <input 
              type="email" 
              name="email"
              placeholder="Email Address" 
              className="p-3 w-full rounded-md border bg-neutral-100 mt-4"
              value={formData.email}
              onChange={handleChange}
              required
            />
            <div className="grid grid-cols-2 gap-4 mt-4">
              <input
                type="text"
                name="phone"
                className="p-3 w-full rounded-md border bg-neutral-100 outline-none"
                value={formData.phone}
                onChange={handlePhoneChange}
                onKeyDown={handlePhoneKeyDown}
                maxLength={14} // +92 plus 10 digits
                required
              />
              <input
                type="text" 
                name="nationality"
                placeholder="Nationality"
                className="p-3 w-full rounded-md border bg-neutral-100"
                value={formData.nationality}
                onChange={handleChange}
              />
            </div>
            
            {/* Updated "I am a..." section */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="flex items-center font-semibold">
                I am a...
              </div>
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center 
                    ${formData.role === 'Student' ? 'border-[#457b9d]' : 'border-gray-300'}`}>
                    {formData.role === 'Student' && (
                      <div className="w-3 h-3 rounded-full bg-[#457b9d]"></div>
                    )}
                  </div>
                  <input 
                    type="radio" 
                    name="role" 
                    value="Student"
                    className="hidden"
                    checked={formData.role === 'Student'}
                    onChange={handleChange}
                    required
                  /> 
                  <span className="text-gray-700">Student</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center 
                    ${formData.role === 'Consultant' ? 'border-[#457b9d]' : 'border-gray-300'}`}>
                    {formData.role === 'Consultant' && (
                      <div className="w-3 h-3 rounded-full bg-[#457b9d]"></div>
                    )}
                  </div>
                  <input 
                    type="radio" 
                    name="role" 
                    value="Consultant" 
                    className="hidden"
                    checked={formData.role === 'Consultant'}
                    onChange={handleChange}
                    required
                  /> 
                  <span className="text-gray-700">Consultant</span>
                </label>
              </div>
            </div>

            <textarea 
              name="message"
              placeholder="What are you looking for?"
              className="p-3 w-full rounded-md border bg-neutral-100 mt-4 h-24"
              value={formData.message}
              onChange={handleChange}
              required
            ></textarea>
            <button 
              type="submit" 
              className="px-8 bg-[#457b9d] text-white py-4 rounded-lg mt-4 hover:bg-[#1d3557] transition-colors duration-300 disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'SENDING...' : 'SUBMIT'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;